import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { COMPANY } from "@/data/site";

const NAV = [
  { to: "/", label: "Overview", testid: "nav-home-link" },
  { to: "/about", label: "Company", testid: "nav-about-link" },
  { to: "/services", label: "What We Do", testid: "nav-services-link" },
  { to: "/projects", label: "Projects", testid: "nav-projects-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-[#0B132B]">
      <header className="fixed top-0 inset-x-0 z-40 border-b border-[rgba(197,160,89,0.2)] bg-[rgba(11,19,43,0.85)] backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
            <div className="w-9 h-9 border border-[#C5A059] flex items-center justify-center">
              <span className="font-serif-display text-[#C5A059] text-lg leading-none">L</span>
            </div>
            <div className="leading-tight">
              <span className="font-serif-display text-xl tracking-wide text-[#F8F9FA] block">
                Lac Léman
              </span>
              <span className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#6C7A89]">
                A Mirabaud Group Branch
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="main-navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative text-sm tracking-wide transition-colors duration-300 pb-1 ${
                    isActive ? "text-[#E6C687]" : "text-[#A0AAB2] hover:text-[#F8F9FA]"
                  } after:absolute after:left-0 after:bottom-0 after:h-px after:bg-[#C5A059] after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            className="md:hidden text-[#E6C687] p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <nav className="md:hidden border-t border-[rgba(197,160,89,0.2)] bg-[#0B132B] px-6 py-4 flex flex-col gap-4" data-testid="mobile-navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#E6C687]" : "text-[#A0AAB2]"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="border-t border-[rgba(197,160,89,0.2)] bg-[#101B3B]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <span className="font-serif-display text-2xl text-[#F8F9FA]">Lac Léman</span>
            <p className="mt-3 text-sm text-[#A0AAB2] leading-relaxed max-w-xs">
              Operational marketing agency of the Mirabaud Group. Serving investment
              projects worldwide since {COMPANY.operatingSince}.
            </p>
          </div>
          <div>
            <span className="font-mono-label text-xs tracking-[0.2em] uppercase text-[#C5A059]">
              Geneva Headquarters
            </span>
            <ul className="mt-4 space-y-3 text-sm text-[#A0AAB2]">
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-[#C5A059] mt-0.5 shrink-0" />
                {COMPANY.address}
              </li>
              <li className="flex items-center gap-3">
                <Phone size={15} className="text-[#C5A059] shrink-0" />
                {COMPANY.phone}
              </li>
              <li className="flex items-center gap-3">
                <Mail size={15} className="text-[#C5A059] shrink-0" />
                {COMPANY.email}
              </li>
            </ul>
          </div>
          <div>
            <span className="font-mono-label text-xs tracking-[0.2em] uppercase text-[#C5A059]">
              Navigate
            </span>
            <ul className="mt-4 space-y-2 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-[#A0AAB2] hover:text-[#E6C687] transition-colors duration-300"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-[rgba(197,160,89,0.15)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#6C7A89]">
            <span>© {new Date().getFullYear()} Lac Léman — A Mirabaud Group operational branch.</span>
            <span>Geneva, Switzerland · Est. 2014</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
